/**
 * Contract between the vidmakr desktop app (apps/desktop) and the web app it
 * hosts. The desktop preload script exposes an object of type `DesktopBridge`
 * as `window.vidmakrDesktop`; in a normal browser it is undefined.
 *
 * Keep this file free of runtime dependencies on DOM or Node APIs: it is
 * bundled into the Electron main/preload scripts and into the web app.
 */
import { extOf, mediaMimeForExt } from './storage-keys';

export type DesktopPlatform = 'darwin' | 'win32' | 'linux';

export type UpdateState = 'disabled' | 'idle' | 'checking' | 'available' | 'downloading' | 'downloaded' | 'not-available' | 'error';

export interface UpdateStatus {
  state: UpdateState;
  /** Version of the update being downloaded or ready to install. */
  version?: string;
  /** Download progress 0..100 while `downloading`. */
  percent?: number;
  /** Error message when `error`. */
  error?: string;
  /** When the last check finished (ms since epoch). */
  checkedAt?: number;
}

/** A mounted drive or volume the desktop app can browse. */
export interface DesktopVolume {
  /** Absolute mount point (`/Volumes/Card`, `E:\`, `/media/me/Card`). */
  path: string;
  /** Display name (volume label or last path segment). */
  name: string;
  /** True for external/removable media (everything but the system disk). */
  removable: boolean;
  totalBytes?: number;
  freeBytes?: number;
  /** Its camera folder (`<path>/DCIM`) when the volume is a camera card. */
  dcim?: string;
}

export type MediaKind = 'video' | 'audio' | 'image';

export interface DesktopDirEntry {
  name: string;
  /** Absolute path; pass back to `listDirectory` / `readFileRange`. */
  path: string;
  isDirectory: boolean;
  /** Media kind for importable files; undefined for directories. */
  kind?: MediaKind;
  sizeBytes: number;
  /** Last modification time, ms since epoch. */
  modifiedAt: number;
}

export interface DesktopDirListing {
  path: string;
  /** The granted root this directory lives in (a volume or a picked folder). */
  root: string;
  /** Parent directory inside the same root, or null at the root. */
  parent: string | null;
  entries: DesktopDirEntry[];
  /** True when the listing was cut off at the entry limit. */
  truncated: boolean;
}

export interface DesktopBridge {
  readonly version: string;
  readonly platform: DesktopPlatform;
  /** Server origin the app is connected to. */
  serverUrl(): Promise<string>;
  /** Shows the "connect to server" screen (switch to a self-hosted server). */
  changeServer(): Promise<void>;

  getUpdateStatus(): Promise<UpdateStatus>;
  onUpdateStatus(cb: (s: UpdateStatus) => void): () => void;
  /** Checks for an update now (also from the app menu). */
  checkForUpdates(): Promise<UpdateStatus>;
  /** Quits and installs a downloaded update. */
  installUpdate(): Promise<void>;

  /** Signs in through the system browser and hands the session back via vidmakr://. */
  signInWithBrowser(redirect?: string): Promise<void>;

  /** Opens a native folder picker; the folder becomes browsable. Null when cancelled. */
  pickFolder(): Promise<string | null>;
  listVolumes(): Promise<DesktopVolume[]>;
  /** Fires whenever a drive is mounted or unmounted. */
  onVolumesChanged(cb: (volumes: DesktopVolume[]) => void): () => void;
  /** Folders the user picked before (still granted). */
  recentFolders(): Promise<string[]>;
  listDirectory(path: string): Promise<DesktopDirListing>;
  /** Reads bytes [start, end) of a media file inside a granted root. */
  readFileRange(path: string, start: number, end: number): Promise<Uint8Array>;
  revealFile(path: string): Promise<void>;
  notify(title: string, body?: string): Promise<void>;
}

const KIND_BY_PREFIX: Record<string, MediaKind> = { video: 'video', audio: 'audio', image: 'image' };

/** Media kind (video/audio/image) of a file name, or null when it is not importable media. */
export function mediaKindOfName(name: string): MediaKind | null {
  if (name.startsWith('.')) return null; // dotfiles, incl. macOS `._foo.mov` resource forks
  const mime = mediaMimeForExt(extOf(name));
  if (!mime) return null;
  return KIND_BY_PREFIX[mime.split('/')[0]] ?? null;
}

/** Deep link scheme registered by the desktop app. */
export const DESKTOP_PROTOCOL = 'vidmakr';
/**
 * The server's desktop download routes (apps/api/src/routes/downloads.ts).
 * The server reads the private repository's releases with its token.
 */
export const DESKTOP_DOWNLOADS_PATH = '/downloads/desktop';
/** electron-updater "generic" feed: latest*.yml, installers and blockmaps. */
export const DESKTOP_UPDATE_FEED_PATH = `${DESKTOP_DOWNLOADS_PATH}/update/`;

export type DesktopDownloadPlatform = 'mac' | 'win' | 'linux';

/** One downloadable installer; `url` is on the vidmakr server. */
export interface DesktopDownload {
  platform: DesktopDownloadPlatform;
  /** arm64 | x64 for mac/win; appimage | deb for linux. */
  variant: string;
  file: string;
  sizeBytes: number;
  url: string;
}

/** `GET /downloads/desktop/latest`. */
export interface DesktopLatestRelease {
  version: string;
  publishedAt: string | null;
  notes?: string;
  downloads: {
    mac: { arm64?: DesktopDownload; x64?: DesktopDownload };
    win: { x64?: DesktopDownload; arm64?: DesktopDownload };
    linux: { appimage?: DesktopDownload; deb?: DesktopDownload };
  };
}
